import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckSquare,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useUsers } from '@/store/user';
import { useTenants } from '@/store/tenant';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import type { User } from '@/types';
import { PageLayout } from '@/components/page-layout';

export const TasksPage = () => {
  const { setBreadcrumbs } = useLayout();
  const navigate = useNavigate();
  const { list: listUsers, isLoading: isLoadingUsers } = useUsers();
  const { tenants, list: listTenants } = useTenants();

  const [pendingUsers, setPendingUsers] = useState<User[]>([]);

  const fetchPendingUsers = async () => {
    try {
      const fetched = await listUsers(undefined, undefined, 'pending');
      setPendingUsers(fetched);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load pending user tasks');
    }
  };

  useEffect(() => {
    setBreadcrumbs([{ name: 'Tasks' }]);
    listTenants();
    fetchPendingUsers();
  }, [setBreadcrumbs]);

  const getTenantName = (tenantId?: string) => {
    if (!tenantId) return 'Unknown Tenant';
    const tenant = tenants.find((t) => t.id === tenantId);
    return tenant ? tenant.name : 'Unknown Tenant';
  };

  if (isLoadingUsers && pendingUsers.length === 0) {
    return (
      <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full flex justify-center items-center h-[50vh]">
        <Loader />
      </div>
    );
  }

  return (
    <PageLayout
      title="Tasks"
      description="Review and process account requests and pending actions."
    >
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900 overflow-hidden shadow-xs">
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white">
            Pending Tasks ({pendingUsers.length})
          </h2>
          <span className="text-xs text-slate-400">
            Tasks requiring administrative action
          </span>
        </div>

        {pendingUsers.length > 0 ? (
          <div className="divide-y divide-slate-100/60 dark:divide-slate-800/60">
            {pendingUsers.map((user) => (
              <div
                key={user.id}
                onClick={() => navigate(`/m/tasks/approvals/user-access/${user.id}`)}
                className="p-5 flex items-center justify-between hover:bg-slate-50/50 dark:hover:bg-slate-900/50 cursor-pointer transition-colors group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-sm border border-amber-500/20">
                    {user.name ? user.name[0].toUpperCase() : user.email[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-slate-900 dark:text-white">
                        {user.name || 'No Name'}
                      </span>
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                        Access Request
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {user.email}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  {(() => {
                    const tenant = tenants.find((t) => t.id === user.tenant_id);
                    return (
                      <div className="flex items-center gap-2.5">
                        {tenant?.logo_url ? (
                          <img
                            src={tenant.logo_url}
                            alt={tenant.name}
                            className="w-6 h-6 rounded object-cover border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                          />
                        ) : null}
                        <div className="text-right">
                          <div className="text-xs font-bold text-slate-700 dark:text-slate-300">
                            {tenant ? tenant.name : getTenantName(user.tenant_id)}
                          </div>
                          <div className="text-[10px] text-slate-400 dark:text-slate-500">
                            Requested Scope
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <CheckSquare className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">All caught up!</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              There are no pending access requests or tasks requiring review.
            </p>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
